import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Separator } from '../ui/separator';
import { 
  Ticket,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Copy,
  CheckCircle,
  Users,
  Calendar,
  Percent,
  DollarSign,
  Eye,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { couponApi } from '../../api/api';

export default function CouponManager() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [saving, setSaving] = useState(false);
  const [redemptionsDialog, setRedemptionsDialog] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [loadingRedemptions, setLoadingRedemptions] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    description: '',
    expires_at: '',
    max_uses: '',
    max_uses_per_user: 1,
    min_plan_price: '',
    is_active: true
  });

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const data = await couponApi.listCoupons();
      setCoupons(data);
    } catch (err) {
      console.error('Failed to fetch coupons:', err);
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      description: '',
      expires_at: '',
      max_uses: '',
      max_uses_per_user: 1,
      min_plan_price: '',
      is_active: true
    });
    setEditingCoupon(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleOpenEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value.toString(),
      description: coupon.description || '',
      expires_at: coupon.expires_at ? coupon.expires_at.split('T')[0] : '',
      max_uses: coupon.max_uses?.toString() || '',
      max_uses_per_user: coupon.max_uses_per_user || 1,
      min_plan_price: coupon.min_plan_price?.toString() || '',
      is_active: coupon.is_active !== false
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code.trim()) {
      toast.error('Coupon code is required');
      return;
    }
    if (!formData.discount_value || parseFloat(formData.discount_value) <= 0) {
      toast.error('Valid discount value is required');
      return;
    }
    if (formData.discount_type === 'percentage' && parseFloat(formData.discount_value) > 100) {
      toast.error('Percentage discount cannot exceed 100%');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        code: formData.code.trim(),
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        description: formData.description || null,
        expires_at: formData.expires_at ? `${formData.expires_at}T23:59:59Z` : null,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        max_uses_per_user: parseInt(formData.max_uses_per_user) || 1,
        min_plan_price: formData.min_plan_price ? parseFloat(formData.min_plan_price) : null,
        is_active: formData.is_active
      };

      if (editingCoupon) {
        await couponApi.updateCoupon(editingCoupon.id, payload);
        toast.success('Coupon updated');
      } else {
        await couponApi.createCoupon(payload);
        toast.success('Coupon created');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchCoupons();
    } catch (err) {
      console.error('Failed to save coupon:', err);
      toast.error(err.response?.data?.detail || 'Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    
    try {
      await couponApi.deleteCoupon(couponId);
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch (err) {
      toast.error('Failed to delete coupon');
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      await couponApi.updateCoupon(coupon.id, { is_active: !coupon.is_active });
      fetchCoupons();
      toast.success(coupon.is_active ? 'Coupon deactivated' : 'Coupon activated');
    } catch (err) {
      toast.error('Failed to update coupon');
    }
  };

  const handleViewRedemptions = async (coupon) => {
    setRedemptionsDialog(coupon);
    setLoadingRedemptions(true);
    try {
      const data = await couponApi.getCouponRedemptions(coupon.id);
      setRedemptions(data);
    } catch (err) {
      toast.error('Failed to load redemptions');
    } finally {
      setLoadingRedemptions(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Coupon code copied!');
  };

  const formatDiscount = (coupon) => {
    if (coupon.discount_type === 'percentage') {
      return `${coupon.discount_value}% OFF`;
    }
    return `$${coupon.discount_value.toFixed(2)} OFF`;
  };

  const isExpired = (coupon) => {
    if (!coupon.expires_at) return false;
    return new Date(coupon.expires_at) < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#6D4AFF]/20 rounded-lg">
                <Ticket className="w-5 h-5 text-[#6D4AFF]" />
              </div>
              <div>
                <CardTitle className="text-white">Coupon Codes</CardTitle>
                <CardDescription className="text-gray-400">
                  Create discount codes for marketing campaigns
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={fetchCoupons}
                variant="outline"
                className="border-[#2d2d44] text-gray-300 hover:bg-[#252540]"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                onClick={handleOpenCreate}
                className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                data-testid="create-coupon-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Coupon
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Coupons List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
        </div>
      ) : coupons.length === 0 ? (
        <Card className="bg-[#1a1a2e] border-[#2d2d44]">
          <CardContent className="py-12 text-center">
            <Ticket className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-white text-lg font-medium mb-2">No Coupons Yet</h3>
            <p className="text-gray-400 mb-4">Create your first coupon code to offer discounts</p>
            <Button
              onClick={handleOpenCreate}
              className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Coupon
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {coupons.map((coupon) => (
            <Card key={coupon.id} className="bg-[#1a1a2e] border-[#2d2d44]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Coupon Code */}
                    <div 
                      className="px-4 py-2 bg-[#252540] rounded-lg border border-dashed border-[#6D4AFF]/50 cursor-pointer hover:border-[#6D4AFF] transition-colors"
                      onClick={() => copyCode(coupon.code)}
                    >
                      <code className="text-[#6D4AFF] font-mono font-bold text-lg tracking-wider">
                        {coupon.code}
                      </code>
                    </div>
                    
                    {/* Discount Badge */}
                    <Badge className={`${
                      coupon.discount_type === 'percentage' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {coupon.discount_type === 'percentage' ? (
                        <Percent className="w-3 h-3 mr-1" />
                      ) : (
                        <DollarSign className="w-3 h-3 mr-1" />
                      )}
                      {formatDiscount(coupon)}
                    </Badge>

                    {/* Status */}
                    {!coupon.is_active ? (
                      <Badge variant="outline" className="border-gray-600 text-gray-400">
                        Inactive
                      </Badge>
                    ) : isExpired(coupon) ? (
                      <Badge variant="outline" className="border-red-500/50 text-red-400">
                        Expired
                      </Badge>
                    ) : (
                      <Badge className="bg-green-500/20 text-green-400">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-6">
                    {/* Stats */}
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1 text-gray-400">
                        <Users className="w-4 h-4" />
                        <span>{coupon.times_used || 0}</span>
                        {coupon.max_uses && <span>/ {coupon.max_uses}</span>}
                      </div>
                      
                      {coupon.expires_at && (
                        <div className="flex items-center gap-1 text-gray-400">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(coupon.expires_at).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleViewRedemptions(coupon)}
                        className="text-gray-400 hover:text-white"
                        data-testid={`view-redemptions-${coupon.code}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyCode(coupon.code)}
                        className="text-gray-400 hover:text-white"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleOpenEdit(coupon)}
                        className="text-gray-400 hover:text-white"
                        data-testid={`edit-coupon-${coupon.code}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(coupon.id)}
                        className="text-gray-400 hover:text-red-400"
                        data-testid={`delete-coupon-${coupon.code}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {coupon.description && (
                  <p className="text-gray-500 text-sm mt-3 pl-2 border-l-2 border-[#2d2d44]">
                    {coupon.description}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-[#1a1a2e] border-[#2d2d44] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ticket className="w-5 h-5 text-[#6D4AFF]" />
              {editingCoupon ? 'Edit Coupon' : 'Create Coupon'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {editingCoupon ? 'Update coupon settings' : 'Create a new discount code'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Code */}
            <div className="space-y-2">
              <Label className="text-gray-300">Coupon Code *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                className="bg-[#252540] border-[#2d2d44] text-white font-mono uppercase"
                placeholder="SUMMER20"
                disabled={!!editingCoupon}
                data-testid="coupon-code-input"
              />
              <p className="text-xs text-gray-500">Letters, numbers, and dashes only</p>
            </div>

            {/* Discount Type & Value */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Discount Type *</Label>
                <Select
                  value={formData.discount_type}
                  onValueChange={(value) => setFormData({ ...formData, discount_type: value })}
                  disabled={!!editingCoupon}
                >
                  <SelectTrigger className="bg-[#252540] border-[#2d2d44] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#252540] border-[#2d2d44]">
                    <SelectItem value="percentage" className="text-white">Percentage (%)</SelectItem>
                    <SelectItem value="fixed" className="text-white">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Value *</Label>
                <div className="relative">
                  <Input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    className="bg-[#252540] border-[#2d2d44] text-white pl-8"
                    placeholder="20"
                    disabled={!!editingCoupon}
                    data-testid="coupon-value-input"
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {formData.discount_type === 'percentage' ? '%' : '$'}
                  </span>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-gray-300">Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-[#252540] border-[#2d2d44] text-white"
                placeholder="Summer sale discount"
                data-testid="coupon-description-input"
              />
            </div>

            <Separator className="bg-[#2d2d44]" />

            {/* Expiration */}
            <div className="space-y-2">
              <Label className="text-gray-300">Expiration Date</Label>
              <Input
                type="date"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                className="bg-[#252540] border-[#2d2d44] text-white"
                data-testid="coupon-expiry-input"
              />
              <p className="text-xs text-gray-500">Leave empty for no expiration</p>
            </div>

            {/* Usage Limits */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Max Total Uses</Label>
                <Input
                  type="number"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  className="bg-[#252540] border-[#2d2d44] text-white"
                  placeholder="Unlimited"
                  data-testid="coupon-max-uses-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Uses Per User</Label>
                <Input
                  type="number"
                  value={formData.max_uses_per_user}
                  onChange={(e) => setFormData({ ...formData, max_uses_per_user: e.target.value })}
                  className="bg-[#252540] border-[#2d2d44] text-white"
                  placeholder="1"
                  data-testid="coupon-per-user-input"
                />
              </div>
            </div>

            {/* Min Plan Price */}
            <div className="space-y-2">
              <Label className="text-gray-300">Minimum Plan Price</Label>
              <div className="relative">
                <Input
                  type="number"
                  value={formData.min_plan_price}
                  onChange={(e) => setFormData({ ...formData, min_plan_price: e.target.value })}
                  className="bg-[#252540] border-[#2d2d44] text-white pl-8"
                  placeholder="No minimum"
                  step="0.01"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-3 bg-[#252540] rounded-lg">
              <div>
                <p className="text-white font-medium">Active</p>
                <p className="text-xs text-gray-500">Allow this coupon to be used</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              className="text-gray-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
              data-testid="save-coupon-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  {editingCoupon ? 'Update' : 'Create'} Coupon
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Redemptions Dialog */}
      <Dialog open={!!redemptionsDialog} onOpenChange={(open) => !open && setRedemptionsDialog(null)}>
        <DialogContent className="bg-[#1a1a2e] border-[#2d2d44] max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-[#6D4AFF]" />
              Redemption History
            </DialogTitle>
            {redemptionsDialog && (
              <DialogDescription className="text-gray-400">
                Coupon: <code className="text-[#6D4AFF]">{redemptionsDialog.code}</code>
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="py-4 max-h-[400px] overflow-y-auto">
            {loadingRedemptions ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 text-[#6D4AFF] animate-spin" />
              </div>
            ) : redemptions.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No redemptions yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {redemptions.map((r) => (
                  <div key={r.id} className="p-3 bg-[#252540] rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-white">{r.user_email}</span>
                      <span className="text-green-400 font-medium">
                        -${r.discount_amount?.toFixed(2)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(r.redeemed_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
